import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PromoCodeCheck, PromoCodeReason } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { usePromoCodeCheck } from '../../hooks/useSubscription';

export interface PromoCodeRowProps {
  planCode: 'PLUS' | 'PRO' | 'CENTER';
  billingCycle: 'MONTHLY' | 'ANNUAL';
  value: { code: string; check: Extract<PromoCodeCheck, { valid: true }> } | null;
  onAccepted: (code: string, check: Extract<PromoCodeCheck, { valid: true }>) => void;
  onCleared: () => void;
}

/**
 * The "Có mã giảm giá?" control shared by UpgradeDialog and SubscriptionTab's
 * chooser (THI-200). One instance per surface, not per tier — the caller picks
 * a single plan/cycle context (`planCode`/`billingCycle`) to check the code
 * against. When that context changes under an ACCEPTED code (the cycle
 * toggle moved), this row re-checks the same code for the new context on its
 * own and hands the caller the fresh result — or the reason it no longer
 * applies, with the code left in the input so the teacher sees what happened.
 * (Johnny's prod smoke 2026-09-06: a 35% code accepted in annual mode was
 * silently dropped on switching to monthly, and the 30% campaign applied.)
 *
 * The check fires only on the "Áp dụng" click: `usePromoCodeCheck` is a
 * mutation, never a query, so nothing round-trips while the teacher types.
 */
export function PromoCodeRow({ planCode, billingCycle, value, onAccepted, onCleared }: PromoCodeRowProps) {
  const { t } = useTranslation('billing');
  const check = usePromoCodeCheck();
  const [expanded, setExpanded] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [reason, setReason] = useState<PromoCodeReason | null>(null);

  // A new plan/cycle context makes a previously-shown error stale (it was
  // never re-checked against the new one). The caller owns clearing an
  // ACCEPTED code (it hands back `value: null` once its own key no longer
  // matches) — this only clears this component's own pending error state, and
  // does so during render rather than an effect: it bails out the instant the
  // key already matches, so it can never loop.
  const contextKey = `${planCode}:${billingCycle}`;
  const [lastKey, setLastKey] = useState(contextKey);
  if (contextKey !== lastKey) {
    setLastKey(contextKey);
    if (reason !== null) setReason(null);
  }

  // Re-validate an accepted code whenever the plan/cycle context moves.
  const prevKey = useRef(contextKey);
  const valueCode = value?.code ?? null;
  useEffect(() => {
    if (prevKey.current === contextKey) return;
    prevKey.current = contextKey;
    if (!valueCode) return;
    check.mutate(
      { code: valueCode, planCode, billingCycle },
      {
        onSuccess: (data) => {
          if (data.valid) {
            onAccepted(valueCode, data);
          } else {
            setCodeInput(valueCode);
            setExpanded(true);
            setReason(data.reason);
            onCleared();
          }
        },
        onError: () => {
          setCodeInput(valueCode);
          setExpanded(true);
          setReason('INVALID');
          onCleared();
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the context change should trigger a re-check
  }, [contextKey]);

  const handleApply = () => {
    const code = codeInput.trim();
    if (!code) return;
    setReason(null);
    check.mutate(
      { code, planCode, billingCycle },
      {
        onSuccess: (data) => {
          if (data.valid) {
            onAccepted(code, data);
          } else {
            setReason(data.reason);
          }
        },
        onError: () => setReason('INVALID'),
      },
    );
  };

  if (value) {
    // Fix wave (B7): the chip must show the CODE's own percentage
    // (`codePercentOff`), never the WINNER's (`percentOff`) — those two
    // diverge whenever a richer campaign beats the code (§2.4 best-of). When
    // that happens, a second line tells the customer what actually got
    // applied instead of silently showing a bigger number than the code
    // itself promises.
    const campaignWon = value.check.percentOff > value.check.codePercentOff;
    return (
      <div className="flex flex-wrap items-center justify-between gap-[10px] rounded-[10px] border border-brand-200 bg-brand-50 px-[14px] py-[10px]">
        <div>
          <p className="text-[13px] text-brand-800">
            {t('offer.applied', { code: value.code, pct: value.check.codePercentOff })}
          </p>
          {campaignWon && (
            <p className="mt-[3px] text-[12px] text-brand-700">
              {t('offer.campaignWins', { name: value.check.name, pct: value.check.percentOff })}
            </p>
          )}
        </div>
        <Button type="button" variant="link" className="min-h-[44px] px-0 text-brand-700" onClick={onCleared}>
          {t('offer.clear')}
        </Button>
      </div>
    );
  }

  if (!expanded) {
    return (
      <Button
        type="button"
        variant="link"
        className="min-h-[44px] px-0 text-surface-500"
        onClick={() => setExpanded(true)}
      >
        {t('offer.haveCode')}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex flex-wrap items-center gap-[8px]">
        <Input
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.toUpperCase().slice(0, 20))}
          placeholder={t('offer.placeholder')}
          maxLength={20}
          className="h-[44px] max-w-[220px] uppercase"
        />
        <Button
          type="button"
          variant="secondary"
          className="min-h-[44px]"
          disabled={check.isPending || !codeInput.trim()}
          onClick={handleApply}
        >
          {t('offer.apply')}
        </Button>
      </div>
      {reason && <p className="text-[12.5px] text-red-700">{t(`offer.reason.${reason}`)}</p>}
    </div>
  );
}
